/**
 * PotCauldron — hero pot visual for the Last-Touch throne hall.
 *
 * Each touch drops a real glowing physics ball from above the rim into the
 * cauldron. The cauldron interior is built from primitive colliders:
 *   - a thin CylinderCollider at the bowl floor catches the orbs
 *   - a ring of CuboidColliders forms the inner wall, keeping orbs in
 * Earlier attempts with TrimeshCollider tunneled (trimeshes are surfaces,
 * not solids — a fast ball can slip through one frame and pop out the
 * other side). HeightfieldCollider crashes the rapier-wasm shape ctor
 * with "unreachable executed" — so we use bulletproof primitives instead.
 *
 * The spawn list is reset when `round.roundId` changes (fresh round →
 * empty pot) and capped at `MAX_PHYSICS_ORBS` so a marathon round can't
 * unbound the rigid-body count.
 */
import { useFrame } from "@react-three/fiber";
import {
  CuboidCollider,
  CylinderCollider,
  RigidBody,
} from "@react-three/rapier";
import { useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";

import { PBRSurfaceMaterial } from "../../scene/PBRSurface";
import type { KothRoundState } from "./useKothRound";

// Cauldron geometry constants in local units (~1m height ranges).
const CAULDRON_BASE_Y = 0.12;          // top of the stone pedestal
const CAULDRON_RIM_Y = 0.62;           // y of the rim above CAULDRON_BASE_Y
const ORB_RADIUS = 0.08;
// Above the bowl's ~70–80 orb capacity so a busy round visibly overflows the
// rim instead of stopping right as it fills; still bounded so the dynamic-body
// count (and physics cost) can't run away on a marathon round.
const MAX_PHYSICS_ORBS = 120;
const SPAWN_HEIGHT_ABOVE_RIM = 0.55;
// Spilled orbs land on this invisible disc at hall-floor level so they always
// pile around the cauldron base — the player world-floor lives in
// ControlsEcctrl and isn't mounted for spectators, so the pot owns its own
// catch rather than relying on it.
const SPILL_APRON_RADIUS = 2.6;

// === Bowl primitives ===
// The visual cauldron interior is a curved bowl: r ≈ 0.28 at the bottom,
// expanding to r ≈ 0.68 at the lip. A single cylindrical wall ring would
// make orbs clip THROUGH the visible cauldron near the bottom (wall too
// far out) and pile up INSIDE the wall higher up (wall too close in). To
// track the curve we use a stack of wall tiers, each a ring at a different
// radius/height. The floor disc catches them at the visible bowl bottom.
//
// Wall thickness ≥ ORB_RADIUS * 2 keeps fast orbs from tunneling.
const BOWL_FLOOR_Y = 0.22;          // local-y of the floor disc top surface
const BOWL_FLOOR_HALF_HEIGHT = 0.04;
const BOWL_FLOOR_RADIUS = 0.22;     // matches lathe interior at bottom

interface BowlTier {
  /** Inner face radius — the smallest distance from axis at this height. */
  innerR: number;
  /** Vertical center of this tier in local coords. */
  centerY: number;
  /** Half-height — tier covers ±halfH around centerY. */
  halfH: number;
}
// Each tier's innerR mirrors the lathe inner-wall radius at its mid-height
// (lathe y + CAULDRON_BASE_Y = group y). With orb radius 0.08, the orb's
// outer surface caps at innerR — so orbs visually sit AGAINST the visible
// inner wall instead of either floating in mid-air (wall too far out) or
// clipping through the visible cauldron (wall too far in).
const BOWL_TIERS: ReadonlyArray<BowlTier> = [
  { innerR: 0.28, centerY: 0.32, halfH: 0.08 }, // lathe (0.28, 0.20)
  { innerR: 0.39, centerY: 0.46, halfH: 0.08 }, // lathe interp
  { innerR: 0.53, centerY: 0.60, halfH: 0.08 }, // lathe interp
  { innerR: 0.65, centerY: 0.74, halfH: 0.06 }, // lathe (0.68, 0.66) – ε
];
const WALL_SEGMENTS = 16;           // segments per tier
const WALL_HALF_THICKNESS = 0.08;   // ≫ ORB_RADIUS so orbs can't tunnel

interface SpawnedOrb {
  id: number;
  spawnX: number;
  spawnZ: number;
  /** Small random horizontal launch velocity so the pile self-levels + spills. */
  velX: number;
  velZ: number;
}

interface PotCauldronProps {
  round: KothRoundState | null;
  position?: [number, number, number];
}

export function PotCauldron({
  round,
  position = [0, 0, 3.8],
}: PotCauldronProps): JSX.Element {
  // Lathe profile for the VISUAL — points trace the outer wall up, across
  // the rim, and back down the inside wall to form a hollow vessel.
  const lathePoints = useMemo(
    () =>
      [
        [0.0001, 0.00],
        [0.55, 0.02],
        [0.78, 0.10],
        [0.85, 0.28],
        [0.85, 0.46],
        [0.72, 0.60],
        [0.78, 0.66],
        [0.82, 0.70],
        [0.74, 0.70],
        [0.68, 0.66],
        [0.55, 0.50],
        [0.40, 0.35],
        [0.28, 0.20],
        [0.0001, 0.09],
      ].map(([x, y]) => new THREE.Vector2(x, y)),
    [],
  );

  // Precompute every wall cuboid (across all tiers). Each cuboid sits tangent
  // to its tier's ring at the right radius/height/orientation. Memoized so
  // React doesn't rebuild rigid bodies on every render.
  interface WallCuboid {
    x: number;
    y: number;
    z: number;
    rotY: number;
    halfH: number;
    halfW: number;
  }
  const wallCuboids = useMemo<WallCuboid[]>(() => {
    const out: WallCuboid[] = [];
    for (const tier of BOWL_TIERS) {
      const centerR = tier.innerR + WALL_HALF_THICKNESS;
      // Each segment's half-width covers the chord between adjacent centers,
      // with 8% slack so neighbors overlap and the wall reads as continuous.
      const halfW = centerR * Math.tan(Math.PI / WALL_SEGMENTS) * 1.08;
      for (let i = 0; i < WALL_SEGMENTS; i++) {
        const angle = (i / WALL_SEGMENTS) * Math.PI * 2;
        out.push({
          x: Math.cos(angle) * centerR,
          y: tier.centerY,
          z: Math.sin(angle) * centerR,
          rotY: -angle,
          halfH: tier.halfH,
          halfW,
        });
      }
    }
    return out;
  }, []);

  const [orbs, setOrbs] = useState<SpawnedOrb[]>([]);
  const nextIdRef = useRef(0);
  const prevRoundIdRef = useRef<string>("none");

  const touchCount = round?.touchCount ?? 0;
  const roundIdKey = round?.roundId.toString() ?? "none";

  // Reconcile orb list with on-chain touchCount + roundId. Fresh round
  // wipes the pot; each new touch appends one orb at a random offset above
  // the rim so multi-touch bursts don't spawn-collide.
  useEffect(() => {
    const roundChanged = prevRoundIdRef.current !== roundIdKey;
    prevRoundIdRef.current = roundIdKey;
    if (roundChanged) nextIdRef.current = 0;

    setOrbs((prev) => {
      const base = roundChanged ? [] : prev;
      const target = Math.min(touchCount, MAX_PHYSICS_ORBS);
      if (target <= base.length) return base;
      const next = [...base];
      let nextId = nextIdRef.current;
      while (next.length < target) {
        // Spread spawns over a disc (not just the center) so a full bowl's
        // surface levels out and orbs landing near the rim tip over the lip,
        // rather than mounding into a cone that never overflows.
        const ang = Math.random() * Math.PI * 2;
        const rad = Math.sqrt(Math.random()) * 0.5;
        next.push({
          id: nextId++,
          spawnX: Math.cos(ang) * rad,
          spawnZ: Math.sin(ang) * rad,
          velX: (Math.random() - 0.5) * 0.9,
          velZ: (Math.random() - 0.5) * 0.9,
        });
      }
      nextIdRef.current = nextId;
      return next;
    });
  }, [touchCount, roundIdKey]);

  const pulseLightRef = useRef<THREE.PointLight>(null);
  useFrame((state) => {
    if (!pulseLightRef.current) return;
    const t = state.clock.elapsedTime;
    // Light grows with orb count so a packed pot glows brighter.
    const fill = Math.min(1, orbs.length / 30);
    pulseLightRef.current.intensity =
      1.0 + fill * 1.4 + Math.sin(t * Math.PI * 1.6) * 0.15;
  });

  return (
    <group position={position}>
      {/* Stone pedestal — visual */}
      <mesh position={[0, 0.06, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[0.85, 1.0, 0.12, 24]} />
        <PBRSurfaceMaterial name="floor-stone" color="#5a4d3e" roughness={0.95} />
      </mesh>

      {/* Pedestal physics — slightly smaller radius so overflow orbs slide
          off the rim and land on the throne-hall floor rather than perching. */}
      <RigidBody type="fixed" position={[0, 0.06, 0]} friction={0.7}>
        <CylinderCollider args={[0.06, 0.92]} />
      </RigidBody>

      {/* Spill apron — invisible floor disc at hall-floor level (top at y≈0)
          that catches orbs overflowing the rim so they pile around the base.
          Coincides with the player world-floor (harmless) and supplies one for
          spectators, who don't mount it. High friction + low restitution so
          spilled orbs settle into a pile instead of scattering. */}
      <RigidBody type="fixed" position={[0, -0.05, 0]} friction={0.9} restitution={0.02}>
        <CylinderCollider args={[0.05, SPILL_APRON_RADIUS]} />
      </RigidBody>

      {/* Cauldron body — visual */}
      <mesh position={[0, CAULDRON_BASE_Y, 0]} castShadow receiveShadow>
        <latheGeometry args={[lathePoints, 32]} />
        <meshStandardMaterial
          color="#2a2218"
          roughness={0.7}
          metalness={0.4}
          side={THREE.DoubleSide}
        />
      </mesh>

      {/* Cauldron physics — solid cylinder floor + ring of cuboid walls.
          Primitive colliders are tunneling-proof: the floor is a thick
          half-space and each wall segment has WALL_HALF_THICKNESS ≫
          ORB_RADIUS so even a fast orb can't slip through. */}
      <RigidBody
        type="fixed"
        position={[0, BOWL_FLOOR_Y, 0]}
        friction={0.85}
        restitution={0.05}
      >
        <CylinderCollider
          args={[BOWL_FLOOR_HALF_HEIGHT, BOWL_FLOOR_RADIUS]}
        />
      </RigidBody>
      {wallCuboids.map((seg, i) => (
        <RigidBody
          key={`wall-${i}`}
          type="fixed"
          position={[seg.x, seg.y, seg.z]}
          rotation={[0, seg.rotY, 0]}
          friction={0.85}
          restitution={0.05}
        >
          <CuboidCollider
            args={[WALL_HALF_THICKNESS, seg.halfH, seg.halfW]}
          />
        </RigidBody>
      ))}

      {/* Iron rim cap — visual only */}
      <mesh
        position={[0, CAULDRON_BASE_Y + CAULDRON_RIM_Y, 0]}
        rotation={[Math.PI / 2, 0, 0]}
        castShadow
      >
        <torusGeometry args={[0.78, 0.04, 8, 32]} />
        <meshStandardMaterial color="#1f1810" roughness={0.6} metalness={0.5} />
      </mesh>

      {/* Three legs — visual only */}
      {[0, 1, 2].map((i) => {
        const ang = (i / 3) * Math.PI * 2;
        return (
          <mesh
            key={i}
            position={[Math.cos(ang) * 0.55, 0.05, Math.sin(ang) * 0.55]}
            castShadow
          >
            <cylinderGeometry args={[0.08, 0.06, 0.1, 8]} />
            <meshStandardMaterial color="#1a140c" roughness={0.7} metalness={0.4} />
          </mesh>
        );
      })}

      {/* Spawned orbs — one rigid body per touch. Tuned to land, settle,
          and stack instead of bounce out of the bowl: low restitution,
          high friction, generous damping. */}
      {orbs.map((o) => (
        <RigidBody
          key={o.id}
          colliders="ball"
          position={[
            o.spawnX,
            CAULDRON_BASE_Y + CAULDRON_RIM_Y + SPAWN_HEIGHT_ABOVE_RIM,
            o.spawnZ,
          ]}
          linearVelocity={[o.velX, 0, o.velZ]}
          restitution={0.08}
          // Lower than the walls' 0.85 so a full pile self-levels and the
          // excess rolls over the rim instead of stacking into a stuck cone.
          friction={0.5}
          linearDamping={0.35}
          angularDamping={0.6}
        >
          <mesh castShadow>
            <sphereGeometry args={[ORB_RADIUS, 14, 14]} />
            <meshStandardMaterial
              color="#fff2c8"
              emissive="#ffd070"
              emissiveIntensity={1.5}
              metalness={0.0}
              roughness={0.35}
              toneMapped={false}
            />
          </mesh>
        </RigidBody>
      ))}

      {/* Warm pulsing light */}
      <pointLight
        ref={pulseLightRef}
        position={[0, CAULDRON_BASE_Y + CAULDRON_RIM_Y + 0.4, 0]}
        color="#ffd070"
        intensity={1.2}
        distance={6}
        decay={1.6}
      />
    </group>
  );
}

export default PotCauldron;
